import { Inject, Injectable, ErrorHandler, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ApplicationError } from '../interfaces/application-error.interface';
import { environment } from 'src/environments/environment';
import { RollbarService } from '../services/rollbar.service';
import { getHttpErrorMessage } from '../utils/http-error-message';

@Injectable()
export class GenericErrorHandler implements ErrorHandler {
  constructor(
    // Because the GeneralErrorHandler is created before the providers, we’ll have to use the Injector to get them.
    private injector: Injector,
    private rollbar: RollbarService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  public handleError(error: any) {
    this.handleChunkLoadFailedError(error);

    /* This is a fix for handling uncaught promise errors */
    if (error.rejection) {
      error = error.rejection as Error;
    }

    if (this.isGeolocationPositionError(error)) {
      console.warn(error);
      return this.notifyGeolocationError(error);
    }

    if (this.isUnauthorizedError(error)) {
      console.warn(error);

      if (environment.rollbar.enable) {
        this.reportToRollbar(error, 'warning');
      }

      return this.notifyError(error);
    }

    console.error(error);

    if (environment.rollbar.enable) {
      this.reportToRollbar(error);
    }

    this.notifyError(error);
  }

  /**
   * HttpErrorResponse doesn't extend Error, so Rollbar's argument parser can't pull a message
   * or stack trace out of it. Passed on its own it's filed under "Item sent with null or missing
   * arguments." with the actual detail buried in the custom data. Passing an explicit message
   * string keeps that detail (still attached as custom data) while giving the item a readable title.
   */
  private reportToRollbar(error: any, level: 'error' | 'warning' = 'error') {
    if (error instanceof HttpErrorResponse) {
      this.rollbar.report(level, this.describeHttpError(error), error);
      return;
    }

    this.rollbar.report(level, error);
  }

  private describeHttpError(error: HttpErrorResponse): string {
    const serverMessage = this.extractServerErrorMessage(error);
    const detail = serverMessage ? ` - ${serverMessage}` : '';

    return `HTTP ${error.status} ${error.statusText || ''} on ${error.url}${detail}`.replace(/\s+/g, ' ').trim();
  }

  private extractServerErrorMessage(error: HttpErrorResponse): string | undefined {
    const body = error.error;

    if (typeof body === 'string') {
      return body;
    }

    return body?.message || body?.error || undefined;
  }

  public handleChunkLoadFailedError(err: any) {
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;

    if (isPlatformBrowser(this.platformId) && chunkFailedMessage.test(err.message)) {
      alert('Nieuwe versie beschikbaar. Update naar de nieuwe versie');
      window.location.reload();
    }
  }

  /**
   * A user denying (or the browser failing to determine) location access is expected behaviour,
   * not an application bug, so it shouldn't be reported to Rollbar. Duck-typed because
   * GeolocationPositionError instances don't reliably pass `instanceof` across browsers.
   */
  private isGeolocationPositionError(error: any): error is GeolocationPositionError {
    return typeof error?.code === 'number' && typeof error?.PERMISSION_DENIED === 'number';
  }

  /**
   * A 401 (e.g. an incorrect login attempt) is expected user error, not an application bug,
   * so it's logged as a warning instead of an error and reported to Rollbar at warning level.
   */
  private isUnauthorizedError(error: any): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse && error.status === 401;
  }

  private notifyGeolocationError(error: GeolocationPositionError) {
    const message =
      error.code === error.PERMISSION_DENIED
        ? 'Geen locatietoestemming gekregen. Schakel locatietoegang in om je positie te tonen.'
        : 'Het is niet mogelijk om je locatie te bepalen.';

    this.openSnackbar(message);
  }

  private notifyError(error: Error | HttpErrorResponse | any) {
    if (error instanceof HttpErrorResponse) {
      return this.openSnackbar(getHttpErrorMessage(error));
    }

    if (error instanceof ApplicationError) {
      return this.openSnackbar(error.message);
    }

    this.openSnackbar(`Er is een fout opgetreden in de applicatie.`);
  }

  // Imported on demand to keep the snackbar out of the main bundle
  private openSnackbar(message: string, action = `OK`) {
    import('@angular/material/snack-bar').then(({ MatSnackBar }) => {
      this.injector.get(MatSnackBar).open(message, action, { duration: 4.5 * 1000 });
    });
  }
}
